// LexDuo · Configuración → Claves API públicas (v74)
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { Key, Copy, ShieldOff, Plus, FileText, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { obtenerToken } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => {
  const tok = obtenerToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const ConfiguracionApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newScopes, setNewScopes] = useState('contacto');
  const [newOrigins, setNewOrigins] = useState('https://www.lexduo.cat');
  const [newEnv, setNewEnv] = useState('live');
  const [createdKey, setCreatedKey] = useState(null);
  const [tab, setTab] = useState('keys');

  const cargarKeys = useCallback(async () => {
    setLoadingKeys(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public-api-keys`, { headers: hdrs() });
      setKeys(r.data?.items || []);
    } catch (e) {
      toast({ title: 'Error cargando keys', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoadingKeys(false); }
  }, []);

  const cargarLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public-api-keys/logs?limit=200`, { headers: hdrs() });
      setLogs(r.data?.items || []);
    } catch (e) {
      toast({ title: 'Error cargando logs', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoadingLogs(false); }
  }, []);

  useEffect(() => { cargarKeys(); cargarLogs(); }, [cargarKeys, cargarLogs]);

  const crearKey = async () => {
    if (!newLabel.trim()) { toast({ title: 'Label requerido', variant: 'destructive' }); return; }
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public-api-keys`,
        { label: newLabel, scopes: newScopes, allowed_origins: newOrigins, env: newEnv },
        { headers: hdrs() });
      setCreatedKey(r.data);
      setShowNew(false);
      setNewLabel(''); setNewOrigins('https://www.lexduo.cat'); setNewScopes('contacto');
      cargarKeys();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const revocar = async (id, label) => {
    if (!window.confirm(`¿Revocar la key "${label}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/public-api-keys/${id}`, { headers: hdrs() });
      toast({ title: 'Key revocada' });
      cargarKeys();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const toggleActivo = async (k) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/public-api-keys/${k.id}`,
        { activo: !k.activo }, { headers: hdrs() });
      cargarKeys();
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast({ title: 'Copiado al portapapeles' });
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="page-api-keys">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Key className="w-6 h-6 text-violet-700" /> Claves API Públicas
        </h1>
        <Button onClick={() => setShowNew(true)} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-new-key">
          <Plus className="w-4 h-4 mr-1" /> Nueva clave
        </Button>
      </div>

      <p className="text-sm text-gray-600">
        Las claves públicas (<code>pk_live_…</code>) permiten que servicios externos (la web pública,
        integraciones de terceros) accedan SOLO a los endpoints <code>/api/public/*</code> con scope
        limitado. <strong>Nunca compartas la clave en HTML público</strong>; úsala server-side (plugin WordPress).
      </p>

      {/* Tabs simple */}
      <div className="flex gap-2 border-b">
        <button onClick={() => setTab('keys')}
                className={`px-3 py-1.5 text-sm ${tab==='keys' ? 'border-b-2 border-violet-700 text-violet-700 font-semibold' : 'text-gray-500'}`}
                data-testid="tab-keys">Claves ({keys.length})</button>
        <button onClick={() => setTab('logs')}
                className={`px-3 py-1.5 text-sm ${tab==='logs' ? 'border-b-2 border-violet-700 text-violet-700 font-semibold' : 'text-gray-500'}`}
                data-testid="tab-logs">Submissions recibidas ({logs.length})</button>
      </div>

      {tab === 'keys' && (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-xs uppercase text-gray-600">
                <th className="p-3">Label</th>
                <th className="p-3">Prefijo</th>
                <th className="p-3">Scopes</th>
                <th className="p-3">Orígenes permitidos</th>
                <th className="p-3 text-right">Usos</th>
                <th className="p-3">Último uso</th>
                <th className="p-3">Estado</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loadingKeys ? (
                <tr><td colSpan={8} className="p-6 text-center text-gray-400">Cargando…</td></tr>
              ) : keys.length === 0 ? (
                <tr><td colSpan={8} className="p-6 text-center text-gray-500 italic">Sin claves. Crea una con "Nueva clave".</td></tr>
              ) : keys.map(k => (
                <tr key={k.id} className="border-b hover:bg-gray-50" data-testid={`row-key-${k.id}`}>
                  <td className="p-3 font-medium">{k.label}</td>
                  <td className="p-3 font-mono text-xs text-gray-700">{k.key_prefix}</td>
                  <td className="p-3"><Badge variant="outline" className="text-xs">{k.scopes}</Badge></td>
                  <td className="p-3 text-xs text-gray-600 max-w-xs truncate" title={k.allowed_origins}>{k.allowed_origins || '— sin restricción —'}</td>
                  <td className="p-3 text-right font-mono">{k.uso_contador}</td>
                  <td className="p-3 text-xs text-gray-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString('es-ES') : '—'}</td>
                  <td className="p-3">
                    <button onClick={() => toggleActivo(k)} className="cursor-pointer" data-testid={`toggle-${k.id}`}>
                      {k.activo
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Activa</Badge>
                        : <Badge className="bg-gray-200 text-gray-700">Inactiva</Badge>}
                    </button>
                  </td>
                  <td className="p-3">
                    <button onClick={() => revocar(k.id, k.label)} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" data-testid={`btn-revocar-${k.id}`}>
                      <ShieldOff className="w-3.5 h-3.5" /> Revocar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'logs' && (
        <Card className="p-0 overflow-hidden">
          <div className="px-3 py-2 border-b flex justify-between items-center">
            <span className="text-sm text-gray-600">Últimas 200 submissions del formulario de contacto público.</span>
            <Button variant="outline" size="sm" onClick={cargarLogs} data-testid="btn-refresh-logs">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-xs uppercase text-gray-600">
                <th className="p-2">Fecha</th>
                <th className="p-2">Ticket</th>
                <th className="p-2">Nombre</th>
                <th className="p-2">Email</th>
                <th className="p-2">Motivo</th>
                <th className="p-2">Origen</th>
                <th className="p-2">Estado</th>
                <th className="p-2">Vinculado</th>
              </tr>
            </thead>
            <tbody>
              {loadingLogs ? (
                <tr><td colSpan={8} className="p-6 text-center text-gray-400">Cargando…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={8} className="p-6 text-center text-gray-500 italic">Sin submissions todavía. Cuando lleguen, aparecerán aquí.</td></tr>
              ) : logs.map(l => (
                <tr key={l.id} className="border-b hover:bg-gray-50 text-xs" data-testid={`row-log-${l.id}`}>
                  <td className="p-2 text-gray-500">{new Date(l.created_at).toLocaleString('es-ES')}</td>
                  <td className="p-2 font-mono">{l.ticket_id}</td>
                  <td className="p-2">{l.nombre}</td>
                  <td className="p-2 text-gray-700">{l.email}</td>
                  <td className="p-2"><Badge variant="outline" className="text-[10px]">{l.motivo}</Badge></td>
                  <td className="p-2 text-gray-500 truncate max-w-[180px]" title={l.origen}>{l.origen}</td>
                  <td className="p-2">
                    {l.estado_proceso === 'pendiente'
                      ? <Badge className="bg-yellow-100 text-yellow-800">Pendiente</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-800">{l.estado_proceso}</Badge>}
                  </td>
                  <td className="p-2">
                    {l.vinculado
                      ? <span className="text-emerald-700 flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> exp #{l.expediente_id || '?'}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal nueva key */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg" data-testid="dialog-new-key">
          <DialogHeader>
            <DialogTitle>Nueva clave API pública</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Label (identificador interno)</Label>
              <Input value={newLabel} onChange={e => setNewLabel(e.target.value)}
                     placeholder="Ej: Web lexduo.cat producción"
                     data-testid="input-new-key-label" />
            </div>
            <div>
              <Label className="text-xs">Scopes (CSV)</Label>
              <Input value={newScopes} onChange={e => setNewScopes(e.target.value)}
                     placeholder="contacto, chat"
                     data-testid="input-new-key-scopes" />
              <p className="text-[11px] text-gray-500 mt-1">Disponibles: <code>contacto</code> (v74), <code>chat</code> (v76+).</p>
            </div>
            <div>
              <Label className="text-xs">Orígenes permitidos (CORS, CSV)</Label>
              <Input value={newOrigins} onChange={e => setNewOrigins(e.target.value)}
                     placeholder="https://www.lexduo.cat, https://lexduo.cat"
                     data-testid="input-new-key-origins" />
              <p className="text-[11px] text-gray-500 mt-1">Dejar vacío = sin restricción (no recomendado).</p>
            </div>
            <div>
              <Label className="text-xs">Entorno</Label>
              <select value={newEnv} onChange={e => setNewEnv(e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      data-testid="select-new-key-env">
                <option value="live">live (producción)</option>
                <option value="test">test (desarrollo)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button onClick={crearKey} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-create-key-submit">Crear clave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal mostrando la key recién creada (UNA sola vez) */}
      <Dialog open={!!createdKey} onOpenChange={() => setCreatedKey(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-key-created">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> Clave creada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-yellow-50 border border-yellow-300 rounded p-3 flex gap-2 text-sm text-yellow-900">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <div>
                <strong>Copia esta clave AHORA.</strong> No se volverá a mostrar.
                Si la pierdes, tendrás que revocarla y generar una nueva.
              </div>
            </div>
            <div>
              <Label className="text-xs">Clave completa</Label>
              <div className="flex gap-1">
                <code className="flex-1 bg-gray-50 border rounded px-3 py-2 font-mono text-xs break-all" data-testid="created-key-text">
                  {createdKey?.key_full}
                </code>
                <Button onClick={() => copy(createdKey?.key_full)} variant="outline" size="sm" data-testid="btn-copy-key">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              Configúrala en <code>wp-admin → Ajustos → LexDuo → Clave API</code> (en la web WordPress).
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedKey(null)} data-testid="btn-close-key-created">Hecho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionApiKeys;
